import type { HansAckerFinding, HansAckerScope, HansAckerSeverity } from './types';

export const PAGE_SIZE = 4;

export const SEVERITIES: { id: HansAckerSeverity; label: string; color: string }[] = [
  { id: 'critical', label: 'Critical', color: '#ef4444' },
  { id: 'high', label: 'High', color: '#f97316' },
  { id: 'medium', label: 'Medium', color: '#f59e0b' },
  { id: 'info', label: 'Informational', color: '#64748b' },
];

export const severityColor = (s: HansAckerSeverity) => SEVERITIES.find((x) => x.id === s)!.color;
export const severityLabel = (s: HansAckerSeverity) => SEVERITIES.find((x) => x.id === s)!.label;

/** Vite build modes in which the Hans-Acker tab is available (never in production). */
export const HANS_ACKER_MODES = ['int', 'development'];

const normalizeName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

export type HansAckerReportKey = 'valet-parking' | 'panorama360';

/**
 * Maps a project name to its bundled Hans-Acker demo report, or null when the tab is unavailable.
 * Names are compared ignoring case and separators, so "Valet Parking", "valet_parking" and
 * "<test>123411" all match their respective runs.
 */
export const hansAckerReportKey = (projectName?: string | null, mode: string = import.meta.env.MODE): HansAckerReportKey | null => {
  if (!HANS_ACKER_MODES.includes(mode) || !projectName) return null;
  const n = normalizeName(projectName);
  if (n.includes('valetparking')) return 'valet-parking';
  if (n.includes('test123411') || n.includes('panorama360')) return 'panorama360';
  return null;
};

/** The Hans-Acker tab is shown only for projects with a bundled report, and only on int / local dev. */
export const isHansAckerProject = (projectName?: string | null, mode: string = import.meta.env.MODE) =>
  hansAckerReportKey(projectName, mode) !== null;

export function filterFindings(
  findings: HansAckerFinding[],
  scope: HansAckerScope,
  severity: HansAckerSeverity | 'all',
) {
  return findings.filter((f) => (scope === 'all' || f.folder === scope) && (severity === 'all' || f.severity === severity));
}

export function severityCounts(findings: HansAckerFinding[]): Record<HansAckerSeverity, number> {
  const counts: Record<HansAckerSeverity, number> = { critical: 0, high: 0, medium: 0, info: 0 };
  findings.forEach((f) => { counts[f.severity] += 1; });
  return counts;
}

export function paginate<T>(items: T[], page: number, size = PAGE_SIZE) {
  const pages = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(Math.max(1, page), pages);
  const start = (current - 1) * size;
  return { items: items.slice(start, start + size), page: current, pages };
}

/** Conic-gradient stops (CSS) for the severity donut. */
export function donutGradient(counts: Record<HansAckerSeverity, number>, fallback = '#64748b') {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (!total) return `conic-gradient(${fallback} 0 100%)`;
  let acc = 0;
  const stops = SEVERITIES.filter((s) => counts[s.id]).map((s) => {
    const from = (acc / total) * 100;
    acc += counts[s.id];
    return `${s.color} ${from}% ${(acc / total) * 100}%`;
  });
  return `conic-gradient(${stops.join(', ')})`;
}

/** Splits a "Code Analysis" section into one markdown block per `**Location N:**`. */
export function splitLocations(markdown: string): string[] {
  return markdown.split(/\n(?=\*\*Location \d+:\*\*)/).map((b) => b.trim()).filter(Boolean);
}

/** Removes the common 2-space indent used inside report location blocks. */
export function dedentBlock(markdown: string): string {
  let inDiff = false;
  return markdown.split('\n').map((l) => {
    const t = l.trim();
    if (t.startsWith('```')) {
      if (inDiff) { inDiff = false; return t; }
      if (t === '```diff') { inDiff = true; return t; }
    }
    return !inDiff && l.startsWith('  ') ? l.slice(2) : l;
  }).join('\n');
}
